// src/Router.tsx
import React, { useEffect } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  useNavigate,
  useLocation,
  type RouteObject,
  Outlet,
} from "react-router-dom";
import { useUser } from "@clerk/clerk-react";
import { toast } from "./components/ui/use-toast";
import { db } from "./services/db";

// Layouts, error boundary and context
import RootLayout from "@/components/layouts/root-layout";
import RouteError from "@/route-error";
import { AccountProvider } from "@/contexts/account-context";
import { CampaignDataProvider } from "@/contexts/campaign-data";
import { OnboardingProvider } from "@/contexts/onboarding-context";
import { OnboardingDataProvider } from "@/contexts/onboarding-data-context";
import OnboardingLayout from "@/components/layouts/onboarding-layout";
import { OnboardingDataTransfer } from "@/components/onboarding-data-transfer";
import { GameStateProvider } from "@/contexts/game-state";
import { PromptMarketplaceProvider } from "@/contexts/prompt-marketplace-context";
import { BFAccountProvider } from "@/contexts/black-friday-account-context";

// Pages - Light pages (keep synchronous for fast loading)
import Index from "@/pages/index";
import BlackFridayDashboardPage from "@/pages/black-friday-dashboard";
import BlackFridaySetupPage from "@/pages/black-friday-setup";
import BlackFridaySettingsPage from "@/pages/black-friday-settings";
import BFTestPage from "@/pages/bf-test";

// Heavy data pages (lazy load to reduce bundle size)
const Analysis = React.lazy(() => import("@/pages/analysis"));
const BudgetPacing = React.lazy(() => import("@/pages/budget-pacing"));
const Trends = React.lazy(() => import("@/pages/trends"));
const Placements = React.lazy(() => import("@/pages/placements"));
const PlacementNetwork = React.lazy(() => import("@/pages/placement-network"));
const Curve = React.lazy(() => import("@/pages/curve"));
const ProfitMax = React.lazy(() => import("@/pages/profitmax"));
const Settings = React.lazy(() => import("@/pages/settings"));
const Hourly = React.lazy(() => import("@/pages/hourly"));

// Intent Mapper pages (lazy load)
const IntentMapperPage = React.lazy(() => import("@/pages/intentmapper"));
const IntentMapperSetupPage = React.lazy(() => import("@/pages/intentmapper-setup"));
import Buy from "@/pages/sales/buy";
import SignInPage from "@/pages/auth/sign-in";
import SignUpPage from "@/pages/auth/sign-up";
import SignUpGatePage from "@/pages/auth/sign-up-gate";
import LearnGoogleAdsScripts from "@/pages/learn/scary";
import ScriptsEntryPage from "@/pages/sales/scripts";
import NotFound from "@/pages/not-found";
import Help from "@/pages/info/help";
import Setup from "@/pages/setup";
import Story from "@/pages/info/story";
import Feedback from "@/pages/feedback";
import WhyPage from "@/pages/info/why";
import AiContextPage from "@/pages/info/ai-context";
import DocumentationPage from "@/pages/info/documentation";
import DocumentViewer from "@/pages/info/document-viewer";
// More heavy data pages (lazy load)
const KeywordsAnalysis = React.lazy(() => import("@/pages/keywords"));
const TreemapVisualization = React.lazy(() => import("@/pages/treemap"));
const DataTest = React.lazy(() => import("@/pages/dev/data-test"));
const WebAppTest = React.lazy(() => import("@/pages/dev/web-app-test"));
const BackendModelTest = React.lazy(
  () => import("@/pages/dev/backend-model-test"),
);
const BackendApiDirectTest = React.lazy(
  () => import("@/pages/dev/backend-api-direct-test"),
);
const OnboardingAITest = React.lazy(
  () => import("@/pages/dev/onboarding-ai-test"),
);
const DemandGenAssetsAnalysis = React.lazy(
  () => import("@/pages/dev/demand-gen-assets"),
);
const UrlMigrationTest = React.lazy(
  () => import("@/pages/dev/url-migration-test"),
);

// Sales pages (keep synchronous for fast loading)
import MasteryWorkshop from "@/pages/sales/mastery";

// Light pages
import AsteroidsGame from "@/pages/asteroids";
import TestSimple from "@/pages/test-simple";
import Privacy from "@/pages/footer/privacy";
import Terms from "@/pages/footer/terms";
import Changelog from "@/pages/footer/changelog";
import LearnNLEScripts from "@/pages/learn/nle";
import ScriptHelp from "@/pages/learn/script-help";
import AutomationPage from "@/pages/learn/automation";
import MobilePage from "@/pages/mobile";
import BuildThePage from "@/pages/sales/build";
import FourCsPage from "@/pages/info/4Cs";
import CPDPage from "@/pages/info/CPD";
import LearnPSAScripts from "@/pages/learn/psa";
import PmaxScript from "@/pages/sales/pmax";
import SlidesPage from "@/pages/sales/slides";
import TreemapPage from "@/pages/members/tree";
import ProgressionsPage from "@/pages/members/progressions";
import GoogleAdsSurvey from "@/pages/sales/survey";
import MembershipPage from "@/pages/sales/ai";
import OwnTheAgentPage from "@/pages/sales/own";
import OtaApplicationPage from "@/pages/sales/ota-application";
// Heavy analysis pages (lazy load)
const PromptMarketplacePage = React.lazy(
  () => import("@/pages/prompt-marketplace"),
);
const PromptDetailPage = React.lazy(() => import("@/pages/prompt-detail"));
const AssetPerformancePage = React.lazy(() => import("@/pages/asset"));
const ProductsPage = React.lazy(() => import("@/pages/products"));
import TwentyPage from "@/pages/sales/twenty";
import BSSPage from "@/pages/sales/bss";
import BSSTestPage from "@/pages/sales/bss-test";
import SIBPage from "@/pages/info/sib";
import OnboardingStartPage from "@/pages/onboarding/start";
import OnboardingAnalysisPage from "@/pages/onboarding/analysis";
import OnboardingResultsPage from "@/pages/onboarding/results";
import OnboardingQuickWinsPage from "@/pages/onboarding/quick-wins";
import QuickWinsPage from "@/pages/wins";
import OnboardingKeywordsPage from "@/pages/onboarding/keywords";
import OnboardingSearchTermsPage from "@/pages/onboarding/search-terms";
import OnboardingToolsChoicePage from "@/pages/onboarding/tools-choice";
import AiSetupPage from "@/pages/onboarding/ai-setup";
import OnboardingAiRunPage from "@/pages/onboarding/ai-run";
import OnboardingAiResultPage from "@/pages/onboarding/ai-result";
import OnboardingBenefitsPage from "@/pages/onboarding/benefits";
import OnboardingGetStartedPage from "@/pages/onboarding/get-started";
import OnboardingScriptChallengePage from "@/pages/onboarding/script-challenge";
import RootCauseAnalysis from "@/pages/dev/root-cause";

// Use Vite's built-in flag so the dev-only routes are reliably enabled during local development.
const DEBUG = import.meta.env.DEV;

// A simple loading spinner
const LoadingState: React.FC = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
  </div>
);

// Suspense wrapper for lazy-loaded components
const SuspenseWrapper: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <React.Suspense fallback={<LoadingState />}>{children}</React.Suspense>;

// Simplified app controller without hooks that could cause issues
const AppController: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isLoaded: isAuthLoaded } = useUser();

  if (!isAuthLoaded) return <LoadingState />;

  return <>{children}</>;
};

// Error handler component
const ErrorStateHandler: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.state?.error) {
      toast({
        variant: "destructive",
        title: "Error Loading Account",
        description: location.state.error,
      });
      // Clear error state
      navigate(location.pathname, { replace: true, state: {} });
      // Optionally clear storage if flagged
      if (location.state.clearStorage) {
        db.delete().then(() => console.log("Cleared corrupted storage"));
      }
    }
  }, [location.state, navigate, location.pathname]);

  return null;
};

// Mobile detection wrapper
const MobileDetectionWrapper: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    );
  };

  // Data pages that should redirect to mobile landing
  const DATA_PAGES = [
    "/analysis",
    "/trends",
    "/placements",
    "/placement-network",
    "/curve",
    "/hourly",
    "/keywords",
    "/asset",
    "/products",
    "/tree",
    "/treemap",
    "/settings",
    "/setup",
    "/prompt-marketplace",
    "/insights",
    "/data-test",
    "/web-app-test",
  ];

  // Mobile-optimized pages (these don't redirect)
  const MOBILE_OPTIMIZED = ["/mobile", "/build", "/buy", "/ai"];

  const isMobile = isMobileDevice();
  const isDataPage = DATA_PAGES.some((path) => location.pathname === path);
  const isOnMobilePage = MOBILE_OPTIMIZED.some((path) =>
    location.pathname.startsWith(path),
  );

  useEffect(() => {
    if (isMobile && isDataPage && !isOnMobilePage) {
      navigate("/mobile", { replace: true });
    }
  }, [location.pathname, navigate, isMobile, isDataPage, isOnMobilePage]);

  return <>{children}</>;
};

// ISOLATED: Onboarding-only providers (NO main app context)
const OnboardingProviders: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return (
    <OnboardingProvider>
      <OnboardingDataProvider>{children}</OnboardingDataProvider>
    </OnboardingProvider>
  );
};

// All routes configuration
const routes: RouteObject[] = [
  {
    path: "/",
    element: (
      <AppController>
        <ErrorStateHandler />
        <MobileDetectionWrapper>
          <Outlet />
        </MobileDetectionWrapper>
      </AppController>
    ),
    errorElement: <RouteError />,
    children: [
      // ISOLATED: Onboarding flow with ONLY onboarding-specific providers
      {
        path: "onboarding/*",
        element: (
          <OnboardingProviders>
            <OnboardingLayout />
          </OnboardingProviders>
        ),
        children: [
          { index: true, element: <OnboardingStartPage /> },
          { path: "start", element: <OnboardingStartPage /> },
          { path: "analysis", element: <OnboardingAnalysisPage /> },
          { path: "results", element: <OnboardingResultsPage /> },
          {
            path: "script-challenge",
            element: <OnboardingScriptChallengePage />,
          },
          { path: "quick-wins", element: <OnboardingQuickWinsPage /> },
          { path: "keywords", element: <OnboardingKeywordsPage /> },
          { path: "search-terms", element: <OnboardingSearchTermsPage /> },
          { path: "tools-choice", element: <OnboardingToolsChoicePage /> },
          { path: "ai-setup", element: <AiSetupPage /> },
          { path: "ai-run", element: <OnboardingAiRunPage /> },
          { path: "ai-result/:insightId", element: <OnboardingAiResultPage /> },
          { path: "benefits", element: <OnboardingBenefitsPage /> },
          {
            path: "get-started",
            element: (
              <>
                <OnboardingDataTransfer />
                <OnboardingGetStartedPage />
              </>
            ),
          },
        ],
      },

      // ISOLATED: Black Friday Dashboard with multi-account support
      {
        path: "bf/*",
        element: (
          <BFAccountProvider>
            <Outlet />
          </BFAccountProvider>
        ),
        children: [
          { index: true, element: <BlackFridayDashboardPage /> },
          { path: "setup", element: <BlackFridaySetupPage /> },
          { path: "settings", element: <BlackFridaySettingsPage /> },
        ],
      },

      // Alias: /black-friday also works (redirects to /bf)
      {
        path: "black-friday/*",
        element: (
          <BFAccountProvider>
            <Outlet />
          </BFAccountProvider>
        ),
        children: [
          { index: true, element: <BlackFridayDashboardPage /> },
          { path: "setup", element: <BlackFridaySetupPage /> },
          { path: "settings", element: <BlackFridaySettingsPage /> },
        ],
      },

      // ISOLATED: Black Friday Test/Debug Page
      ...(DEBUG
        ? [
            {
              path: "bf-test",
              element: <BFTestPage />,
            },
          ]
        : []),

      // Fast routes with just AccountProvider (for navbar)
      {
        path: "*",
        element: (
          <AccountProvider>
            <RootLayout />
          </AccountProvider>
        ),
        children: [
          // Basic auth and info routes
          { path: "sign-in", element: <SignInPage /> },
          { path: "sign-up", element: <SignUpPage /> },
          { path: "auth/sign-up", element: <SignUpPage /> },
          { path: "auth/sign-in", element: <SignInPage /> },
          { path: "auth/sign-up-gate", element: <SignUpGatePage /> },
          { path: "privacy", element: <Privacy /> },
          { path: "terms", element: <Terms /> },
          { path: "changelog", element: <Changelog /> },
          { path: "asteroids", element: <AsteroidsGame /> },

          // Home page - should load fast
          { index: true, element: <Index /> },

          // Info pages
          { path: "story", element: <Story /> },
          { path: "why", element: <WhyPage /> },
          { path: "ai-context", element: <AiContextPage /> },
          { path: "sib", element: <SIBPage /> },
          { path: "documentation", element: <DocumentationPage /> },
          { path: "documentation/:documentId", element: <DocumentViewer /> },
          {
            path: "documentation/:documentId/bullet-points",
            element: <DocumentViewer />,
          },

          // Tutorial/learning pages
          {
            path: "nle",
            element: (
              <GameStateProvider>
                <LearnNLEScripts />
              </GameStateProvider>
            ),
          },
          {
            path: "psa",
            element: (
              <GameStateProvider>
                <LearnPSAScripts />
              </GameStateProvider>
            ),
          },
          {
            path: "script-help",
            element: (
              <GameStateProvider>
                <ScriptHelp />
              </GameStateProvider>
            ),
          },
          {
            path: "scary",
            element: (
              <GameStateProvider>
                <LearnGoogleAdsScripts />
              </GameStateProvider>
            ),
          },
          { path: "automation", element: <AutomationPage /> },

          // ----------------------------------------------------------------
          // Dev-only utilities (placed BEFORE the generic data-heavy wrapper)
          // ----------------------------------------------------------------
          ...(DEBUG
            ? [
                {
                  path: "data-test",
                  element: (
                    <CampaignDataProvider>
                      <DataTest />
                    </CampaignDataProvider>
                  ),
                },
                {
                  path: "demand-gen-assets",
                  element: (
                    <CampaignDataProvider>
                      <DemandGenAssetsAnalysis />
                    </CampaignDataProvider>
                  ),
                },
                {
                  path: "root-cause",
                  element: (
                    <CampaignDataProvider>
                      <RootCauseAnalysis />
                    </CampaignDataProvider>
                  ),
                },
                {
                  path: "dev/url-migration-test",
                  element: (
                    <SuspenseWrapper>
                      <UrlMigrationTest />
                    </SuspenseWrapper>
                  ),
                },
              ]
            : []),

          // Sales pages
          { path: "mobile", element: <MobilePage /> },
          { path: "build", element: <BuildThePage /> },
          { path: "pmax", element: <PmaxScript /> },
          { path: "mastery", element: <MasteryWorkshop /> },
          { path: "scripts", element: <ScriptsEntryPage /> },
          { path: "buy", element: <Buy /> },
          { path: "ai", element: <MembershipPage /> },
          { path: "4cs", element: <FourCsPage /> },
          { path: "cpd", element: <CPDPage /> },
          { path: "slides", element: <SlidesPage /> },
          { path: "survey", element: <GoogleAdsSurvey /> },
          { path: "own", element: <OwnTheAgentPage /> },
          { path: "ota-application", element: <OtaApplicationPage /> },
          { path: "20", element: <TwentyPage /> },
          { path: "bss", element: <BSSPage /> },

          // Member pages
          { path: "progressions", element: <ProgressionsPage /> },

          // Basic pages that don't need heavy data loading
          { path: "help", element: <Help /> },
          { path: "feedback", element: <Feedback /> },
          { path: "test-simple", element: <TestSimple /> },
          {
            path: "web-app-test",
            element: (
              <SuspenseWrapper>
                <WebAppTest />
              </SuspenseWrapper>
            ),
          },
          {
            path: "backend-model-test",
            element: (
              <SuspenseWrapper>
                <BackendModelTest />
              </SuspenseWrapper>
            ),
          },
          {
            path: "backend-api-direct",
            element: (
              <SuspenseWrapper>
                <BackendApiDirectTest />
              </SuspenseWrapper>
            ),
          },
          {
            path: "onboarding-ai-test",
            element: (
              <SuspenseWrapper>
                <OnboardingAITest />
              </SuspenseWrapper>
            ),
          },

          // Intent Mapper (hidden page, uses account selector)
          {
            path: "intentmapper",
            element: (
              <CampaignDataProvider>
                <SuspenseWrapper>
                  <IntentMapperPage />
                </SuspenseWrapper>
              </CampaignDataProvider>
            ),
          },
          {
            path: "intentmapper-setup",
            element: (
              <SuspenseWrapper>
                <IntentMapperSetupPage />
              </SuspenseWrapper>
            ),
          },

          // ProfitMax - standalone calculator (no campaign data needed)
          {
            path: "profitmax",
            element: (
              <SuspenseWrapper>
                <ProfitMax />
              </SuspenseWrapper>
            ),
          },

          // Data-heavy routes that need full contexts
          {
            path: "*",
            element: (
              <CampaignDataProvider>
                <Outlet />
              </CampaignDataProvider>
            ),
            children: [
              {
                path: "analysis",
                element: (
                  <SuspenseWrapper>
                    <Analysis />
                  </SuspenseWrapper>
                ),
              },
              {
                path: "budget",
                element: (
                  <SuspenseWrapper>
                    <BudgetPacing />
                  </SuspenseWrapper>
                ),
              },
              {
                path: "trends",
                element: (
                  <SuspenseWrapper>
                    <Trends />
                  </SuspenseWrapper>
                ),
              },
              {
                path: "placements",
                element: (
                  <SuspenseWrapper>
                    <Placements />
                  </SuspenseWrapper>
                ),
              },
              {
                path: "placement-network",
                element: (
                  <SuspenseWrapper>
                    <PlacementNetwork />
                  </SuspenseWrapper>
                ),
              },
              {
                path: "curve",
                element: (
                  <SuspenseWrapper>
                    <Curve />
                  </SuspenseWrapper>
                ),
              },
              {
                path: "hourly",
                element: (
                  <SuspenseWrapper>
                    <Hourly />
                  </SuspenseWrapper>
                ),
              },
              {
                path: "keywords",
                element: (
                  <SuspenseWrapper>
                    <KeywordsAnalysis />
                  </SuspenseWrapper>
                ),
              },
              { path: "wins", element: <QuickWinsPage /> },
              {
                path: "asset",
                element: (
                  <SuspenseWrapper>
                    <AssetPerformancePage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: "products",
                element: (
                  <SuspenseWrapper>
                    <ProductsPage />
                  </SuspenseWrapper>
                ),
              },
              { path: "tree", element: <TreemapPage /> },
              {
                path: "treemap",
                element: (
                  <SuspenseWrapper>
                    <TreemapVisualization />
                  </SuspenseWrapper>
                ),
              },
              {
                path: "settings",
                element: (
                  <SuspenseWrapper>
                    <Settings />
                  </SuspenseWrapper>
                ),
              },
              { path: "setup", element: <Setup /> },
              {
                path: "prompt/:promptId",
                element: (
                  <SuspenseWrapper>
                    <PromptMarketplaceProvider>
                      <PromptDetailPage />
                    </PromptMarketplaceProvider>
                  </SuspenseWrapper>
                ),
              },
              {
                path: "prompt-marketplace",
                element: (
                  <SuspenseWrapper>
                    <PromptMarketplaceProvider>
                      <PromptMarketplacePage />
                    </PromptMarketplaceProvider>
                  </SuspenseWrapper>
                ),
              },
              {
                path: "insights",
                element: (
                  <SuspenseWrapper>
                    <PromptMarketplaceProvider>
                      <PromptMarketplacePage />
                    </PromptMarketplaceProvider>
                  </SuspenseWrapper>
                ),
              },

              // Dev routes (only in development)
              ...(DEBUG
                ? [
                    {
                      path: "data-test",
                      element: (
                        <SuspenseWrapper>
                          <DataTest />
                        </SuspenseWrapper>
                      ),
                    },
                  ]
                : []),
            ],
          },

          // Catch all - must be last
          { path: "*", element: <NotFound /> },
        ],
      },
    ],
  },
];

// Create the browser router instance
const browserRouter = createBrowserRouter(routes);

export { browserRouter as router };
